import React, { useState, useEffect } from 'react';
import './RiderHistory.css';

const CURRENT_YEAR = new Date().getFullYear();
const TOTAL_YEARS = 5;
const DEFAULT_YEARS = Array.from({ length: TOTAL_YEARS }, (_, i) => CURRENT_YEAR - 1 - i);

function hasKnownResults(yearData) {
    if (!yearData || !yearData.IsParticipant) return false;
    return yearData.Points != null ||
        yearData.HasFinished != null ||
        yearData.TopResult != null ||
        (Array.isArray(yearData.Results) && yearData.Results.some(Boolean));
}

function getCompetitionYears(items = []) {
    const currentYearData = items.find(item => item.EventYear === CURRENT_YEAR);
    const newestYear = hasKnownResults(currentYearData) ? CURRENT_YEAR : CURRENT_YEAR - 1;
    return Array.from({ length: TOTAL_YEARS }, (_, i) => newestYear - i);
}

// Session cache for rider history data
const riderHistoryCache = {};

function getRankDisplay(entry) {
    if (!entry) return null;
    if (entry.resultType === 2) return { text: 'DNF', cls: 'rank-dnf' };
    const v = entry.rank?.Value;
    if (!v) return null;
    if (v === 1) return { text: '1', cls: 'rank-gold' };
    if (v === 2) return { text: '2', cls: 'rank-silver' };
    if (v === 3) return { text: '3', cls: 'rank-bronze' };
    if (v <= 20) return { text: `${v}`, cls: 'rank-top20' };
    return { text: `${v}`, cls: 'rank-normal' };
}

function getRaceColor(compId) {
    if (compId === 38) return '#ff69b4'; // Giro pink
    if (compId === 25) return '#fadb00'; // Tour yellow
    if (compId === 39) return '#da291c'; // Vuelta red
    return '#888';
}

function RiderHistory({ riderName, riderId, teamAbbr, qualities, gameMode, competitions, apiUrl, onShowTop20, onClose, modalId }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const procyclingstatsUrl = qualities?.procyclingstats_url;

    const openRiderLink = (event) => {
        if (!procyclingstatsUrl) return;
        event.preventDefault();
        event.stopPropagation();
        window.open(procyclingstatsUrl, '_blank', 'noopener,noreferrer');
    };

    useEffect(() => {
        if (!riderId) {
            const emptyData = gameMode !== 'klassiekers' ? [] : {};
            setResults(emptyData);
            setLoading(false);
            return;
        }

        const cacheKey = `${riderId}_${gameMode}`;
        // Check cache first
        if (riderHistoryCache[cacheKey]) {
            setResults(riderHistoryCache[cacheKey]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        fetch(`${apiUrl}/rider-history/${riderId}?game=${gameMode}`)
            .then(r => r.json())
            .then(data => {
                if (data.ResultCode === 0) {
                    if (gameMode !== 'klassiekers') {
                        const rawItems = data.Content?.Items || [];
                        riderHistoryCache[cacheKey] = rawItems;
                        setResults(rawItems);
                    } else {
                        // Build map: competitionId -> { year -> rank }
                        const map = {};
                        (data.Content?.Items || []).forEach(item => {
                            map[item.CompetitionId] = {};
                            (item.Results || []).forEach(r => {
                                map[item.CompetitionId][r.Year] = {
                                    rank: r.Rank,
                                    resultType: r.ResultType
                                };
                            });
                        });
                        // Store in cache
                        riderHistoryCache[cacheKey] = map;
                        setResults(map);
                    }
                } else {
                    setResults(gameMode !== 'klassiekers' ? [] : {});
                }
                setLoading(false);
            })
            .catch(() => {
                setResults(gameMode !== 'klassiekers' ? [] : {});
                setLoading(false);
            });
    }, [riderId, apiUrl, gameMode]);

    // Show ALL competitions
    const competitionsToShow = competitions;

    const hasAnyGiroResults = React.useMemo(() => {
        if (gameMode === 'klassiekers' || !results || !Array.isArray(results)) return false;
        return results.some(compData => {
            const items = compData.Items || [];
            const competitionYears = getCompetitionYears(items);
            return items.some(item => competitionYears.includes(item.EventYear) && item.IsParticipant);
        });
    }, [results, gameMode]);

    const allQualitiesZero = React.useMemo(() => {
        if (!qualities) return false;
        const q = qualities;
        return (Number(q.klassement) || 0) === 0 && 
               (Number(q.klimmen) || 0) === 0 && 
               (Number(q.tijdrijden) || 0) === 0 && 
               (Number(q.sprinten) || 0) === 0 && 
               (Number(q.punchen) || 0) === 0 && 
               (Number(q.heuvels) || 0) === 0 && 
               (Number(q.kasseien) || 0) === 0;
    }, [qualities]);

    return (
        <div className="rider-history-overlay"
            onMouseDown={e => e.stopPropagation()}
            onClick={(e) => { onClose(e); }}>
            <div className="rider-history-modal modal-box"
                data-modal-id={modalId}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}>
                <div className="rider-history-header">
                    <h3>
                        {procyclingstatsUrl ? (
                            <button
                                type="button"
                                className="rider-history-name-link"
                                title="Open ProCyclingStats-profiel"
                                onClick={openRiderLink}
                            >
                                {riderName}
                            </button>
                        ) : (
                            riderName
                        )}
                        {teamAbbr && <span className="rider-team-abbr">({teamAbbr})</span>}
                    </h3>
                    <button className="rider-history-close" onClick={(e) => { 
                        e.preventDefault(); 
                        e.stopPropagation(); 
                        setTimeout(onClose, 50); 
                    }}>✕</button>
                </div>

                {loading && (
                    <div className="rider-history-loading">
                        <div className="spinner" />
                        <span>Uitslagen laden...</span>
                    </div>
                )}

                {error && (
                    <div className="rider-history-error">{error}</div>
                )}

                {!loading && !error && results && gameMode !== 'klassiekers' && (
                    <div className="rider-history-table-wrapper giro-tables-wrapper">
                        {!hasAnyGiroResults ? (
                            <div className="geen-resultaten">Geen eerdere resultaten</div>
                        ) : (
                            competitionsToShow.map(comp => {
                                const compData = results.find(r => r.CompetitionId === comp.CompetitionId);
                                const items = compData?.Items || [];
                                const competitionYears = getCompetitionYears(items);
                                
                                const hasAnyParticipation = competitionYears.some(y => {
                                    const yearData = items.find(i => i.EventYear === y);
                                    return yearData && yearData.IsParticipant;
                                });

                                if (!hasAnyParticipation) {
                                    return null;
                                }
                                
                                return (
                                    <div key={comp.CompetitionId} className="giro-history-section">
                                        <h4 className="giro-history-title">
                                            <span className="race-dot" style={{ backgroundColor: getRaceColor(comp.CompetitionId) }}></span>
                                            {comp.TranslatedName}
                                        </h4>
                                        <table className="rider-history-table giro-history-table">
                                            <colgroup>
                                                <col style={{ width: '15%' }} />
                                                <col style={{ width: '17%' }} />
                                                <col style={{ width: '17%' }} />
                                                <col style={{ width: '17%' }} />
                                                <col style={{ width: '17%' }} />
                                                <col style={{ width: '17%' }} />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th>Jaar</th>
                                                    <th>ALG</th>
                                                    <th>PUNT</th>
                                                    <th>BERG</th>
                                                    <th>TOP 20</th>
                                                    <th>PT</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {competitionYears.map(y => {
                                                    const yearData = items.find(i => i.EventYear === y);
                                                    if (!yearData || !yearData.IsParticipant) {
                                                        return (
                                                            <tr key={y}>
                                                                <td>{y}</td>
                                                                <td className="rank-empty">—</td>
                                                                <td className="rank-empty">—</td>
                                                                <td className="rank-empty">—</td>
                                                                <td className="rank-empty">—</td>
                                                                <td className="rank-empty">—</td>
                                                            </tr>
                                                        );
                                                    }
                                                    
                                                    let alg = "—";
                                                    let punt = "—";
                                                    let berg = "—";
                                                    
                                                    if (yearData.HasFinished === false) {
                                                        alg = "DNF";
                                                    }
                                                    
                                                    const getRankStr = (resultsArr, classId) => {
                                                        if (!resultsArr) return null;
                                                        const res = resultsArr.find(r => r && r.ClassificationId === classId);
                                                        if (res && res.Rank && res.Rank.Value > 0) return res.Rank.Value;
                                                        return null;
                                                    };
                                                    
                                                    if (yearData.HasFinished !== false) {
                                                        const algVal = getRankStr(yearData.Results, 3);
                                                        if (algVal) alg = algVal;
                                                    }
                                                    
                                                    const puntVal = getRankStr(yearData.Results, 10);
                                                    if (puntVal) punt = puntVal;
                                                    
                                                    const bergVal = getRankStr(yearData.Results, 23);
                                                    if (bergVal) berg = bergVal;
                                                    
                                                    const topRes = yearData.TopResult || {};
                                                    const top20 = topRes.AmountOfTopFinishes || 0;
                                                    const stageWins = topRes.AmountOfStageWins || 0;
                                                    
                                                    const points = yearData.Points != null ? yearData.Points : "—";
                                                    
                                                    const renderCell = (val) => {
                                                        if (val === "DNF") return <td className="rank-dnf">DNF</td>;
                                                        if (val === "—") return <td className="rank-empty">—</td>;
                                                        if (val === 1) return <td className="rank-gold">1</td>;
                                                        if (val === 2) return <td className="rank-silver">2</td>;
                                                        if (val === 3) return <td className="rank-bronze">3</td>;
                                                        return <td className="rank-normal">{val}</td>;
                                                    };
                                                    
                                                    return (
                                                        <tr key={y}>
                                                            <td>{y}</td>
                                                            {renderCell(alg)}
                                                            {renderCell(punt)}
                                                            {renderCell(berg)}
                                                            <td className="rank-normal">
                                                                {top20 > 0 ? top20 : "—"}
                                                                {stageWins > 0 && <span style={{color: '#4ade80', marginLeft: '4px', fontWeight: 'bold'}}>({stageWins})</span>}
                                                            </td>
                                                            <td className="rank-normal">{points}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {!loading && !error && results && gameMode === 'klassiekers' && (
                    <div className="rider-history-table-wrapper">
                        <table className="rider-history-table">
                            <colgroup>
                                <col /> {/* race name col: auto */}
                                {DEFAULT_YEARS.map(y => (
                                    <col key={y} style={{ width: '52px' }} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="race-col">Wedstrijd</th>
                                    {DEFAULT_YEARS.map(y => (
                                        <th key={y}>{y}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {competitionsToShow.length === 0 ? (
                                    <tr>
                                        <td colSpan={DEFAULT_YEARS.length + 1} className="no-results">
                                            Geen uitslagen gevonden.
                                        </td>
                                    </tr>
                                ) : (
                                    competitionsToShow.map(comp => {
                                        const compResults = results[comp.CompetitionId] || {};
                                        return (
                                            <tr key={comp.CompetitionId}>
                                                <td
                                                    className={`race-col ${gameMode === 'klassiekers' ? 'clickable-race' : ''}`}
                                                    onClick={gameMode === 'klassiekers' ? () => onShowTop20(comp.TranslatedName, comp.CompetitionId) : undefined}
                                                    title={gameMode === 'klassiekers' ? `Klik voor Top 20: ${comp.TranslatedName}` : undefined}
                                                >
                                                    {comp.TranslatedName}
                                                </td>
                                                {DEFAULT_YEARS.map(y => {
                                                    const rank = compResults[y];
                                                    const display = getRankDisplay(rank);
                                                    return (
                                                        <td key={y} className={display ? display.cls : 'rank-empty'}>
                                                            {display ? display.text : '—'}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {gameMode !== 'klassiekers' && qualities && !loading && (
                    <div className="rider-qualities-section">
                        <h4>Kwaliteiten</h4>
                        {qualities.klassement === 0.1 ? (
                            <div className="nader-te-bepalen">Nader te bepalen</div>
                        ) : allQualitiesZero ? (
                            <div className="werkt-aan-kwaliteiten">Deze renner werkt nog aan zijn kwaliteiten</div>
                        ) : (
                            <div className="qualities-list">
                                {[
                                    { label: 'Klassement', val: qualities.klassement || 0 },
                                    { label: 'Klimmen', val: qualities.klimmen || 0 },
                                    { label: 'Tijdrijden', val: qualities.tijdrijden || 0 },
                                    { label: 'Sprinten', val: qualities.sprinten || 0 },
                                    { label: 'Punchen', val: qualities.punchen || 0 },
                                    { label: 'Heuvels', val: qualities.heuvels || 0 },
                                    { label: 'Kasseien', val: qualities.kasseien || 0 }
                                ]
                                    .filter(q => q.val > 0)
                                    .sort((a, b) => b.val - a.val)
                                    .map((q, idx) => {
                                        const renderStars = (val) => {
                                            const starCount = val / 2;
                                            const fullStars = Math.floor(starCount);
                                            const hasHalfStar = val % 2 !== 0;
                                            const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

                                            return (
                                                <>
                                                    {'★'.repeat(fullStars)}
                                                    {hasHalfStar && <span className="half-star">★</span>}
                                                    <span style={{ opacity: 0.2 }}>{'★'.repeat(emptyStars)}</span>
                                                </>
                                            );
                                        };

                                        return (
                                            <div key={idx} className="quality-item">
                                                <span className="quality-label">{q.label}</span>
                                                <div className="quality-stars">
                                                    {renderStars(q.val)}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default RiderHistory;
